/** Global search (F-10). Records only — Help articles are a client-side F-11 index. */

export const SEARCH_HIT_KINDS = [
  'event',
  'asset',
  'tote',
  'bin',
  'trailer',
  'shipment',
  'user',
] as const;
export type SearchHitKind = (typeof SEARCH_HIT_KINDS)[number];

export const SEARCH_GROUP_LIMIT = 8;
export const SEARCH_TOTAL_LIMIT = 20;

export interface SearchHit {
  kind: SearchHitKind;
  id: string;
  title: string;
  subtitle: string;
  href: string;
  /** Exact tote number or asset serial (case-insensitive). */
  exact: boolean;
}

export interface SearchResponse {
  events: SearchHit[];
  assets: SearchHit[];
  totes: SearchHit[];
  bins: SearchHit[];
  trailers: SearchHit[];
  shipments: SearchHit[];
  users: SearchHit[];
}

export function emptySearchResponse(): SearchResponse {
  return {
    events: [],
    assets: [],
    totes: [],
    bins: [],
    trailers: [],
    shipments: [],
    users: [],
  };
}

export function flattenSearchHits(body: SearchResponse): SearchHit[] {
  return [
    ...body.events,
    ...body.assets,
    ...body.totes,
    ...body.bins,
    ...body.trailers,
    ...body.shipments,
    ...body.users,
  ];
}

/** Exact tote/serial first, then remaining hits in group order. */
export function rankRecordHits(body: SearchResponse): SearchHit[] {
  const all = flattenSearchHits(body);
  const exactTote = all.find((hit) => hit.kind === 'tote' && hit.exact);
  const exactAsset = all.find((hit) => hit.kind === 'asset' && hit.exact);
  const rest = all.filter((hit) => hit !== exactTote && hit !== exactAsset);
  return [...(exactTote ? [exactTote] : []), ...(exactAsset ? [exactAsset] : []), ...rest];
}

export function trimSearchResponse(
  body: SearchResponse,
  total = SEARCH_TOTAL_LIMIT,
): SearchResponse {
  const ranked = rankRecordHits(body);
  const kept = new Set(ranked.slice(0, total).map((hit) => `${hit.kind}:${hit.id}`));
  const take = (hits: SearchHit[]): SearchHit[] =>
    hits.filter((hit) => kept.has(`${hit.kind}:${hit.id}`));
  return {
    events: take(body.events),
    assets: take(body.assets),
    totes: take(body.totes),
    bins: take(body.bins),
    trailers: take(body.trailers),
    shipments: take(body.shipments),
    users: take(body.users),
  };
}
